import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, Drawer, IconButton, Modal, Paper, Stack, Typography, useTheme } from '@mui/material';
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ContextualAiLayerContext } from './ContextualAiLayerContext';

interface ContextualAiDialogProps {
    open: boolean;
    onClose: () => void;
    anchorEl?: HTMLElement | null;
    title?: string;
    workflow: 'position-generation' | 'template-generation' | 'template-editor' | 'success-profile-generation' | 'ongoing-assessment';
    children: ReactNode;
}

interface AnchoredGeometry { top: number; left: number; width: number; height: number; }

const SAFE_GUTTER = 16;
const ANCHOR_GAP = 10;
const MIN_ANCHORED_WIDTH = 400;
const MIN_ANCHORED_HEIGHT = 360;
const MIN_WIDE_VIEWPORT = 1600;
const MAX_WIDTH = 560;

const finiteOr = (value: number, fallback: number) => Number.isFinite(value) ? value : fallback;

export const calculateContextualAiGeometry = (
    anchor: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
): AnchoredGeometry | null => {
    if (viewportWidth < MIN_WIDE_VIEWPORT || viewportHeight <= SAFE_GUTTER * 2)
        return null;

    const anchorTop = finiteOr(anchor.top, 0);
    const anchorHeight = Math.max(0, finiteOr(anchor.height, 0));
    const anchorBottom = finiteOr(anchor.bottom, anchorTop + anchorHeight);
    const anchorRight = finiteOr(anchor.right, 0);
    const anchorWidth = Math.max(0, finiteOr(anchor.width, 0));
    const anchorLeft = finiteOr(anchor.left, anchorRight - anchorWidth);

    const intersectsViewport = anchorRight > 0
        && anchorLeft < viewportWidth
        && anchorBottom > 0
        && anchorTop < viewportHeight;
    if (!intersectsViewport)
        return null;

    const left = Math.max(SAFE_GUTTER, anchorRight + ANCHOR_GAP);
    const availableWidth = viewportWidth - SAFE_GUTTER - left;
    if (availableWidth < MIN_ANCHORED_WIDTH)
        return null;

    const top = Math.max(SAFE_GUTTER, anchorTop);
    const visibleBottom = Math.min(viewportHeight - SAFE_GUTTER, anchorBottom);
    const availableHeight = Math.max(0, visibleBottom - top);
    if (availableHeight < MIN_ANCHORED_HEIGHT)
        return null;

    return {
        left,
        top,
        width: Math.min(MAX_WIDTH, availableWidth),
        height: availableHeight,
    };
};

const sameGeometry = (current: AnchoredGeometry | null, next: AnchoredGeometry | null) =>
    current === next
    || (current !== null
        && next !== null
        && current.top === next.top
        && current.left === next.left
        && current.width === next.width
        && current.height === next.height);

/** A shared, portaled AI dialog that never participates in host-editor layout. */
const ContextualAiDialog = ({ open, onClose, anchorEl, title = 'Synergist AI', workflow, children }: ContextualAiDialogProps) => {
    const [geometry, setGeometry] = useState<AnchoredGeometry | null>(null);
    const [transientCount, setTransientCount] = useState(0);
    const transientCountRef = useRef(0);
    const measureFrameRef = useRef<number | null>(null);
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const onCloseRef = useRef(onClose);
    const theme = useTheme();
    const openTransient = useCallback(() => {
        transientCountRef.current += 1;
        setTransientCount(transientCountRef.current);
    }, []);
    const closeTransient = useCallback(() => {
        transientCountRef.current = Math.max(0, transientCountRef.current - 1);
        setTransientCount(transientCountRef.current);
    }, []);
    const layers = useMemo(() => ({
        surfaceZIndex: theme.zIndex.modal + 2,
        transientZIndex: theme.zIndex.modal + 4,
        openTransient,
        closeTransient,
    }), [closeTransient, openTransient, theme.zIndex.modal]);
    onCloseRef.current = onClose;

    useLayoutEffect(() => {
        if (!open || !anchorEl) {
            setGeometry(current => sameGeometry(current, null) ? current : null);
            return;
        }

        const measure = () => {
            const next = calculateContextualAiGeometry(
                anchorEl.getBoundingClientRect(),
                window.innerWidth,
                window.innerHeight,
            );
            setGeometry(current => sameGeometry(current, next) ? current : next);
        };

        let disposed = false;
        const scheduleMeasure = () => {
            if (disposed || measureFrameRef.current !== null)
                return;
            measureFrameRef.current = window.requestAnimationFrame(() => {
                measureFrameRef.current = null;
                if (!disposed)
                    measure();
            });
        };

        // Establish the initial surface before paint. Subsequent geometry drift
        // is coalesced to one measurement per animation frame.
        measure();
        window.addEventListener('resize', scheduleMeasure);
        // Capture-phase scroll observes nested editor/dialog scroll owners even
        // though scroll does not bubble.
        window.addEventListener('scroll', scheduleMeasure, true);
        const observer = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(scheduleMeasure);
        observer?.observe(anchorEl);

        return () => {
            disposed = true;
            observer?.disconnect();
            window.removeEventListener('resize', scheduleMeasure);
            window.removeEventListener('scroll', scheduleMeasure, true);
            if (measureFrameRef.current !== null) {
                window.cancelAnimationFrame(measureFrameRef.current);
                measureFrameRef.current = null;
            }
        };
    }, [anchorEl, open]);

    useEffect(() => {
        if (!open)
            return;
        openerRef.current = document.activeElement as HTMLElement;
        const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);
        return () => {
            window.clearTimeout(focusTimer);
            openerRef.current?.focus();
        };
    }, [open]);

    const handleModalClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
        // Menus, pickers, and error dialogs opened by the AI surface own Escape
        // until they close. This also protects non-modal transients that opt into
        // ContextualAiLayerContext.
        if (reason === 'escapeKeyDown' && transientCountRef.current > 0)
            return;
        onCloseRef.current();
    };

    const content = (
        <ContextualAiLayerContext.Provider value={layers}>
        <Stack sx={{ height: '100%', minHeight: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 56, px: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography component="h2" variant="h6" fontWeight={750} sx={{ flex: 1 }}>{title}</Typography>
                <IconButton ref={closeRef} aria-label="Close Synergist AI" onClick={onClose} edge="end"><CloseRounded /></IconButton>
            </Stack>
            <Box data-testid="contextual-ai-scroll-region" sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>{children}</Box>
        </Stack>
        </ContextualAiLayerContext.Provider>
    );

    if (!open)
        return null;
    if (geometry) {
        return (
            <Modal
                open
                onClose={handleModalClose}
                disableEnforceFocus={transientCount > 0}
                sx={{ zIndex: layers.surfaceZIndex }}
                BackdropProps={{ invisible: true }}
                data-testid="contextual-ai-modal"
            >
                <Paper
                    role="dialog"
                    aria-modal="true"
                    tabIndex={-1}
                    aria-label={title}
                    data-ai-workflow={workflow}
                    data-testid="contextual-ai-dialog"
                    elevation={18}
                    sx={{ position: 'fixed', ...geometry, overflow: 'hidden', border: 1, borderColor: 'divider' }}
                >
                    {content}
                </Paper>
            </Modal>
        );
    }
    return (
        <Drawer
            data-testid="contextual-ai-dialog"
            data-ai-workflow={workflow}
            anchor="right"
            open
            onClose={handleModalClose}
            sx={{ zIndex: layers.surfaceZIndex }}
            ModalProps={{ keepMounted: true, disableEnforceFocus: transientCount > 0 }}
            PaperProps={{ role: 'dialog', 'aria-modal': 'true', 'aria-label': title, 'data-ai-workflow': workflow, sx: { width: { xs: '100%', sm: 'min(520px, calc(100vw - 32px))' }, maxWidth: '100vw', height: '100dvh', overflow: 'hidden' } } as any}
        >
            {content}
        </Drawer>
    );
};

export default ContextualAiDialog;
